import React, { useEffect, useState } from 'react';

/**
 * An <img> for an endpoint behind the bearer token.
 *
 * Every media route in this app sits behind `authMiddleware`, which reads the
 * Authorization header and nothing else — so a plain `<img src="/api/...">`
 * against one of them 401s and renders a broken image. This fetches the bytes
 * with the header, hands the blob to an object URL, and shows that instead.
 *
 * The cache is module-level and keyed by URL because the calendar, the board and
 * the list all show the same reference images: without it, switching view
 * refetches every thumbnail on screen.
 */
const cache = new Map();
const inflight = new Map();

async function load(url, token) {
  if (cache.has(url)) return cache.get(url);
  if (inflight.has(url)) return inflight.get(url);

  const p = (async () => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(String(res.status));
    const objectUrl = URL.createObjectURL(await res.blob());
    cache.set(url, objectUrl);
    return objectUrl;
  })().finally(() => inflight.delete(url));

  inflight.set(url, p);
  return p;
}

function AuthedImage({ src, token, alt = '', className, fallback = null, ...rest }) {
  const [resolved, setResolved] = useState(() => cache.get(src) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src || !token) return undefined;
    const cached = cache.get(src);
    if (cached) { setResolved(cached); return undefined; }

    let live = true;
    setResolved(null);
    setFailed(false);
    load(src, token)
      .then((url) => { if (live) setResolved(url); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [src, token]);

  if (failed || !src) return fallback;
  // Hold the space while it loads rather than letting the layout jump when a
  // grid of thumbnails resolves one at a time.
  if (!resolved) return <span className={className} aria-hidden="true" {...rest} />;
  return <img src={resolved} alt={alt} className={className} {...rest} />;
}

export default AuthedImage;
